import { useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { errorMessage, post } from "../../lib/api";
import { useMe, useRefreshMe } from "../../lib/session";
import { Alert, Button, Card, Field, Input, PageHeader } from "../../components/ui";

/** Change password and set up MFA. Also the forced first-login step for platform admins (AUTH-5/7). */
export function SecurityPage() {
  const me = useMe();
  const refresh = useRefreshMe();
  const navigate = useNavigate();
  const { required } = useSearch({ strict: false }) as { required?: string };
  const user = me.data!.user;
  const state = me.data!.sessionState;

  return (
    <div className="mx-auto max-w-2xl px-4 py-8">
      <PageHeader title="Security" subtitle={user.email} />
      {required && state === "password_change_required" && <Alert tone="warn" title="Choose a new password">You need to set your own password before continuing.</Alert>}
      {required && state === "mfa_enrolment_required" && <Alert tone="warn" title="Set up two-step verification">Platform admins must use an authenticator app.</Alert>}
      {required && state === "ok" && (
        <Alert tone="ok">
          You're all set.{" "}
          <button className="underline" onClick={() => navigate({ to: "/" })}>
            Continue
          </button>
        </Alert>
      )}
      <PasswordCard onDone={refresh} />
      <MfaCard enabled={user.mfaEnabled} isAdmin={user.isPlatformAdmin} onDone={refresh} />
    </div>
  );
}

function PasswordCard({ onDone }: { onDone: () => void }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [msg, setMsg] = useState<{ tone: "ok" | "danger"; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <Card title="Password" className="mb-6">
      {msg && <Alert tone={msg.tone}>{msg.text}</Alert>}
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setMsg(null);
          try {
            await post("/api/auth/password/change", { currentPassword: current, newPassword: next });
            setMsg({ tone: "ok", text: "Password changed. Your other sessions were signed out." });
            setCurrent("");
            setNext("");
            onDone();
          } catch (err) {
            setMsg({ tone: "danger", text: errorMessage(err) });
          } finally {
            setBusy(false);
          }
        }}
      >
        <Field label="Current password" htmlFor="cur">
          <Input id="cur" type="password" autoComplete="current-password" required value={current} onChange={(e) => setCurrent(e.target.value)} />
        </Field>
        <Field label="New password" htmlFor="new" hint="At least 12 characters, not your email.">
          <Input id="new" type="password" autoComplete="new-password" minLength={12} required value={next} onChange={(e) => setNext(e.target.value)} />
        </Field>
        <Button type="submit" loading={busy}>
          Change password
        </Button>
      </form>
    </Card>
  );
}

function MfaCard({ enabled, isAdmin, onDone }: { enabled: boolean; isAdmin: boolean; onDone: () => void }) {
  const [setup, setSetup] = useState<{ secret: string; otpauthUrl: string } | null>(null);
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [disable, setDisable] = useState({ password: "", code: "" });

  if (codes)
    return (
      <Card title="Save your recovery codes">
        <Alert tone="warn">Each code works once. Store them somewhere safe; they won't be shown again.</Alert>
        <div className="bg-surface-2 mb-4 grid grid-cols-2 gap-2 rounded-lg p-4 font-mono text-sm">
          {codes.map((c) => (
            <span key={c}>{c}</span>
          ))}
        </div>
        <Button
          onClick={() => {
            navigator.clipboard?.writeText(codes.join("\n"));
          }}
          variant="secondary"
        >
          Copy codes
        </Button>{" "}
        <Button onClick={() => setCodes(null)}>Done</Button>
      </Card>
    );

  return (
    <Card title="Two-step verification">
      {error && <Alert tone="danger">{error}</Alert>}
      {enabled ? (
        <>
          <p className="mb-4 text-sm">
            <span className="text-ok font-medium">On.</span> You'll be asked for a code from your authenticator app when you sign in.
          </p>
          {!isAdmin && (
            <form
              className="grid gap-3 sm:grid-cols-3"
              onSubmit={async (e) => {
                e.preventDefault();
                setBusy(true);
                setError(null);
                try {
                  await post("/api/auth/mfa/disable", disable);
                  onDone();
                } catch (err) {
                  setError(errorMessage(err));
                } finally {
                  setBusy(false);
                }
              }}
            >
              <Input type="password" placeholder="Password" aria-label="Password" required value={disable.password} onChange={(e) => setDisable({ ...disable, password: e.target.value })} />
              <Input placeholder="Current code" aria-label="Current code" required value={disable.code} onChange={(e) => setDisable({ ...disable, code: e.target.value })} />
              <Button type="submit" variant="danger" loading={busy}>
                Turn off
              </Button>
            </form>
          )}
        </>
      ) : !setup ? (
        <>
          <p className="mb-4 text-sm">Protect your account with a code from an authenticator app (1Password, Google Authenticator, Authy…).</p>
          <Button
            loading={busy}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                setSetup(await post("/api/auth/mfa/setup"));
              } catch (err) {
                setError(errorMessage(err));
              } finally {
                setBusy(false);
              }
            }}
          >
            Set up authenticator app
          </Button>
        </>
      ) : (
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            setError(null);
            try {
              const r = await post<{ recoveryCodes: string[] }>("/api/auth/mfa/enable", { code });
              setCodes(r.recoveryCodes);
              onDone();
            } catch (err) {
              setError(errorMessage(err));
            } finally {
              setBusy(false);
            }
          }}
        >
          <ol className="mb-4 list-decimal space-y-2 pl-5 text-sm">
            <li>
              Add an account in your authenticator app using this key:
              <div className="bg-surface-2 mt-1 rounded p-2 font-mono text-sm break-all select-all">{setup.secret}</div>
              <a className="text-accent text-xs underline" href={setup.otpauthUrl}>
                Or open it in an authenticator app on this device
              </a>
            </li>
            <li>Enter the 6-digit code it shows.</li>
          </ol>
          <Field label="Code" htmlFor="totp">
            <Input id="totp" inputMode="numeric" autoComplete="one-time-code" required value={code} onChange={(e) => setCode(e.target.value)} />
          </Field>
          <Button type="submit" loading={busy}>
            Turn on
          </Button>
        </form>
      )}
    </Card>
  );
}
